import { Pill } from '@HoudiniUI/Pill';
import { css } from 'aphrodite';
import { Link } from 'react-router-dom';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { OpenNewWindowIcon } from '../../../web/components/svgs/icons/OpenNewWindowIcon';
import { getVisibleSettingsCards, ISettingsCard, SettingsSectionType } from '../../models/setting';
import { SettingIcons } from './settingIcons';
import { hueStyleSheet, styleSheet } from './styles';

type SectionHue = keyof typeof hueStyleSheet;

const Sections: { hue: SectionHue; title: string; type: SettingsSectionType }[] = [
	{ hue: 'orange', title: 'Gamification', type: SettingsSectionType.Gamification },
	{ hue: 'purple', title: 'Coaching & AI', type: SettingsSectionType.CoachingAndAi },
	{ hue: 'teal', title: 'Reporting', type: SettingsSectionType.Reporting },
	{ hue: 'blue', title: 'Calling setup', type: SettingsSectionType.CallingSetup },
	{ hue: 'gray', title: 'Account', type: SettingsSectionType.Account },
	{ hue: 'gray', title: 'Your account', type: SettingsSectionType.YourAccount },
	{ hue: 'blue', title: 'Calling', type: SettingsSectionType.Calling },
	{ hue: 'purple', title: 'Coaching', type: SettingsSectionType.Coaching },
	{ hue: 'orange', title: 'Rewards', type: SettingsSectionType.Rewards },
];

const SettingsHomeCard = ({ card, hue }: { card: ISettingsCard; hue: SectionHue }) => {
	const content = (
		<>
			<span className={css(styleSheet.iconTile, hueStyleSheet[hue])}>
				{SettingIcons[card.settingType]?.(css(styleSheet.icon))}
			</span>
			<span className={css(styleSheet.cardText)}>
				<span className={css(styleSheet.cardName)}>{card.text}</span>
				<span className={css(styleSheet.cardDescription)}>{card.description}</span>
			</span>
		</>
	);

	if (card.href) {
		return (
			<a className={css(styleSheet.card)} href={card.href} target='_blank' rel='noreferrer'>
				{content}
				<OpenNewWindowIcon className={css(styleSheet.externalIcon)} fill='currentColor' />
			</a>
		);
	}

	return (
		<Link className={css(styleSheet.card)} to={card.to || `/settings/${card.settingType}`}>
			{content}
		</Link>
	);
};

export const SettingsHome = () => {
	const { account } = useUserSession();
	const isAdmin = !!account?.isAdmin;
	const cards = getVisibleSettingsCards({
		achievementsEnabled: !!account?.features?.aida?.achievementsEnabled,
		isAdmin,
		isLevitateSalesCoffeeAccount: !!account?.isLevitateSalesCoffeeAccount,
	});
	const sections = Sections.map(section => ({
		...section,
		cards: cards.filter(card => card.section === section.type),
	})).filter(section => section.cards.length > 0);

	return (
		<div className={css(styleSheet.page)}>
			<div className={css(styleSheet.content)}>
				<div className={css(styleSheet.header)}>
					<h1 className={css(styleSheet.title)}>Settings</h1>
					<p className={css(styleSheet.subtitle)}>
						{isAdmin ? 'Admin settings for your Coffee account' : 'Your Coffee settings'}
					</p>
				</div>
				{sections.map(section => (
					<section className={css(styleSheet.section)} key={section.type}>
						<div className={css(styleSheet.sectionHeader)}>
							<h2 className={css(styleSheet.sectionTitle)}>{section.title}</h2>
							{section.cards.every(card => !!card.href) ? (
								<Pill variant='label' color='gray' label='Opens in Levitate' />
							) : null}
						</div>
						<div className={css(styleSheet.grid)}>
							{section.cards.map(card => (
								<SettingsHomeCard card={card} hue={section.hue} key={card.settingType} />
							))}
						</div>
					</section>
				))}
			</div>
		</div>
	);
};
